import { Copy, Link2 } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { api, getErrorMessage, type Invitation } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import { userHasGlobalScope } from "../auth/scopes.ts";
import { useAuth } from "../contexts/AuthContext.tsx";
import { copyToClipboard } from "../utils/clipboard.ts";
import { OrgRolesChecklist } from "./OrgRoles.tsx";

export const invitationLink = (token: string) => `${window.location.origin}/invitations/${token}`;

const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

interface OrgInviteDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName?: string;
}

/**
 * Bring someone into an organization by email: they get an invitation link to
 * share. Platform staff can also add someone who already has an account; for an
 * org admin the backend refuses that, so nobody is pulled in without consent.
 */
function OrgInviteDialog({ open, onClose, organizationId, organizationName }: OrgInviteDialogProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAddExisting = userHasGlobalScope(user, "users:manage");
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<string[]>(["member"]);
  const [created, setCreated] = useState<Invitation | null>(null);

  const invite = useMutation({
    mutationFn: () => api.inviteOrgMember(organizationId, { email: email.trim(), roles }),
    meta: { suppressErrorToast: true },
    onSuccess: (result) => {
      if (result.status === "added") {
        queryClient.invalidateQueries({ queryKey: keys.organizationMembership.byOrg(organizationId) });
        toast.success(`${result.member.user_display_name || result.member.user_email} added`);
      } else {
        queryClient.invalidateQueries({ queryKey: keys.orgInvitation.byOrg(organizationId) });
        setCreated(result.invitation);
      }
      close();
    },
  });

  function close() {
    onClose();
    setEmail("");
    setRoles(["member"]);
    invite.reset();
  }

  const canSubmit = looksLikeEmail(email) && roles.length > 0 && !invite.isPending;

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) invite.mutate();
          }}
        >
          <DialogTitle>Invite people</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <TextField
                autoFocus
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (invite.isError) invite.reset();
                }}
                helperText={
                  canAddExisting
                    ? `Already on ScaleupXQ? They're added to ${organizationName ?? "the organization"} right away. Otherwise they get an invitation link.`
                    : "They get an invitation link to create their account."
                }
              />
              <div>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Roles
                </Typography>
                <OrgRolesChecklist value={roles} onChange={setRoles} disabled={invite.isPending} />
              </div>
              {invite.isError && <Alert severity="error">{getErrorMessage(invite.error)}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              Invite
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={!!created} onClose={() => setCreated(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitation created</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this link with <strong>{created?.email}</strong> so they can create their account.
            It expires in 7 days, and stays under Pending invitations until then.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            value={created ? invitationLink(created.token) : ""}
            slotProps={{
              input: {
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Link2 fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreated(null)}>Done</Button>
          <Button
            variant="contained"
            startIcon={<Copy />}
            onClick={() => created && void copyToClipboard(invitationLink(created.token), "Link copied")}
          >
            Copy link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OrgInviteDialog;
